import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import type { Equipment } from "../hooks/useEquipments"
import type { EquipmentIssueStat } from "../hooks/useEquipmentIssueStats"

function formatDate(value?: string) {
  if (!value) return "-"
  const d = new Date(value)
  if (isNaN(d.getTime())) return "-"
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`
}

const EMPTY_STAT: EquipmentIssueStat = {
  issued: 0,
  checking: 0,
  resolved: 0,
  unavailable: 0,
  total: 0,
  latestActivityAt: "",
  hasUpdate: false,
}

// 이슈 상태별 개수 뱃지 (0건은 흐리게 표시)
function StatBadge({
  label,
  count,
  tone,
}: {
  label: string
  count: number
  tone: "issued" | "checking" | "resolved"
}) {
  const active = count > 0
  const toneClass = !active
    ? "bg-gray-50 text-gray-300 border-gray-100"
    : tone === "issued"
    ? "bg-red-50 text-red-700 border-red-200"
    : tone === "checking"
    ? "bg-amber-50 text-amber-700 border-amber-200"
    : "bg-emerald-50 text-emerald-700 border-emerald-200"

  return (
    <span
      title={`${label} ${count}건`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium whitespace-nowrap ${toneClass}`}
    >
      {label}
      <span className={`font-bold tabular-nums ${active ? "" : "text-gray-300"}`}>{count}</span>
    </span>
  )
}

// 최근 변경/신규 이슈 표시
function UpdateFlag() {
  return (
    <span
      title="최근 등록되었거나 상태가 변경된 이슈가 있습니다"
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500 text-white text-[11px] font-bold whitespace-nowrap shadow-sm"
    >
      <span className="relative flex h-1.5 w-1.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white"></span>
      </span>
      NEW
    </span>
  )
}

interface SortableRowProps {
  eq: Equipment
  canManage: boolean
  sortEnabled: boolean
  stat?: EquipmentIssueStat
  onOpenIssue: (eq: Equipment) => void
  onEdit: (eq: Equipment) => void
  onDelete: (eq: Equipment) => void
}

// 데스크톱 테이블 행 (드래그 정렬 지원)
export function SortableRow({
  eq,
  canManage,
  sortEnabled,
  stat,
  onOpenIssue,
  onEdit,
  onDelete,
}: SortableRowProps) {
  const s = stat || EMPTY_STAT
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: eq._id,
    disabled: !sortEnabled,
  })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: "relative",
    zIndex: isDragging ? 10 : "auto",
  }

  return (
    <tr
      ref={setNodeRef}
      style={style}
      onClick={() => onOpenIssue(eq)}
      className={`transition-colors cursor-pointer ${
        s.hasUpdate ? "bg-orange-50/60 hover:bg-orange-100/60" : "bg-white hover:bg-orange-50/40"
      }`}
    >
      {canManage && (
        <td className="px-2 py-3 w-8" onClick={(e) => e.stopPropagation()}>
          <span
            {...(sortEnabled ? attributes : {})}
            {...(sortEnabled ? listeners : {})}
            className={`inline-flex items-center justify-center text-gray-400 ${
              sortEnabled
                ? "cursor-grab active:cursor-grabbing hover:text-gray-600"
                : "opacity-30 cursor-not-allowed"
            }`}
            title={sortEnabled ? "드래그하여 순서 변경" : "검색/필터 해제 후 순서를 변경할 수 있습니다"}
          >
            <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
              <circle cx="7" cy="5" r="1.5" />
              <circle cx="13" cy="5" r="1.5" />
              <circle cx="7" cy="10" r="1.5" />
              <circle cx="13" cy="10" r="1.5" />
              <circle cx="7" cy="15" r="1.5" />
              <circle cx="13" cy="15" r="1.5" />
            </svg>
          </span>
        </td>
      )}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          {s.hasUpdate ? (
            <span className="w-1.5 h-6 rounded-full bg-orange-500 shrink-0" aria-hidden="true" />
          ) : null}
          <span className="font-medium text-gray-900 group-hover:text-orange-600">
            {eq.productName}
          </span>
          {s.hasUpdate ? <UpdateFlag /> : null}
        </div>
      </td>
      <td className="px-4 py-3 text-gray-600">{eq.supplier || "-"}</td>
      <td className="px-4 py-3 text-gray-600">{eq.customerMgmtNumber || "-"}</td>
      <td className="px-4 py-3 text-gray-600">{formatDate(eq.deliveryDate)}</td>
      <td className="px-4 py-3">
        {s.total > 0 ? (
          <div className="flex flex-wrap items-center gap-1">
            <StatBadge label="이슈등록" count={s.issued} tone="issued" />
            <StatBadge label="확인중" count={s.checking} tone="checking" />
            <StatBadge label="조치완료" count={s.resolved} tone="resolved" />
          </div>
        ) : (
          <span className="text-gray-400 text-xs">이슈 없음</span>
        )}
      </td>
      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{eq.creatorName || "-"}</td>
      {canManage && (
        <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-end gap-1">
            <button
              onClick={() => onEdit(eq)}
              className="px-2.5 py-1 text-xs font-medium text-orange-700 bg-orange-50 rounded-md hover:bg-orange-100 transition-colors"
            >
              수정
            </button>
            <button
              onClick={() => onDelete(eq)}
              className="px-2.5 py-1 text-xs font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
            >
              삭제
            </button>
          </div>
        </td>
      )}
    </tr>
  )
}

interface SortableCardProps {
  eq: Equipment
  canManage: boolean
  sortEnabled: boolean
  stat?: EquipmentIssueStat
  onOpenIssue: (eq: Equipment) => void
  onEdit: (eq: Equipment) => void
  onDelete: (eq: Equipment) => void
}

// 모바일 카드 (드래그 정렬 지원)
export function SortableCard({
  eq,
  canManage,
  sortEnabled,
  stat,
  onOpenIssue,
  onEdit,
  onDelete,
}: SortableCardProps) {
  const s = stat || EMPTY_STAT
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: eq._id,
    disabled: !sortEnabled,
  })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : "auto",
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={() => onOpenIssue(eq)}
      className={`rounded-xl border shadow-sm p-4 cursor-pointer transition-colors ${
        s.hasUpdate
          ? "bg-orange-50/70 border-orange-300 active:bg-orange-100/70"
          : "bg-white border-gray-200 active:bg-orange-50/40"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {canManage && (
            <span
              onClick={(e) => e.stopPropagation()}
              {...(sortEnabled ? attributes : {})}
              {...(sortEnabled ? listeners : {})}
              className={`inline-flex items-center justify-center text-gray-400 shrink-0 ${
                sortEnabled
                  ? "cursor-grab active:cursor-grabbing hover:text-gray-600"
                  : "opacity-30 cursor-not-allowed"
              }`}
              title={sortEnabled ? "드래그하여 순서 변경" : "검색/필터 해제 후 순서를 변경할 수 있습니다"}
            >
              <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                <circle cx="7" cy="5" r="1.5" />
                <circle cx="13" cy="5" r="1.5" />
                <circle cx="7" cy="10" r="1.5" />
                <circle cx="13" cy="10" r="1.5" />
                <circle cx="7" cy="15" r="1.5" />
                <circle cx="13" cy="15" r="1.5" />
              </svg>
            </span>
          )}
          <span className="font-semibold text-gray-900 min-w-0">
            {eq.productName}
          </span>
          {s.hasUpdate ? <UpdateFlag /> : null}
        </div>
        {canManage && (
          <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => onEdit(eq)}
              className="px-2 py-1 text-xs font-medium text-orange-700 bg-orange-50 rounded-md"
            >
              수정
            </button>
            <button
              onClick={() => onDelete(eq)}
              className="px-2 py-1 text-xs font-medium text-red-600 bg-red-50 rounded-md"
            >
              삭제
            </button>
          </div>
        )}
      </div>
      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
        <dt className="text-gray-400">납품업체</dt>
        <dd className="text-gray-700">{eq.supplier || "-"}</dd>
        <dt className="text-gray-400">관리번호</dt>
        <dd className="text-gray-700">{eq.customerMgmtNumber || "-"}</dd>
        <dt className="text-gray-400">납품일자</dt>
        <dd className="text-gray-700">{formatDate(eq.deliveryDate)}</dd>
        <dt className="text-gray-400">등록자</dt>
        <dd className="text-gray-700">{eq.creatorName || "-"}</dd>
      </dl>
      <div className="mt-3 pt-3 border-t border-gray-100">
        {s.total > 0 ? (
          <div className="flex flex-wrap items-center gap-1">
            <StatBadge label="이슈등록" count={s.issued} tone="issued" />
            <StatBadge label="확인중" count={s.checking} tone="checking" />
            <StatBadge label="조치완료" count={s.resolved} tone="resolved" />
          </div>
        ) : (
          <span className="text-gray-400 text-xs">등록된 이슈가 없습니다</span>
        )}
      </div>
    </div>
  )
}
